import { MathUtils } from 'three';
import type { CharacterDefinition } from '../config/characterConfig';
import { TOOL_CONFIG, type ToolId } from '../config/toolConfig';
import type { RandomManager } from '../core/RandomManager';

export interface RescueFactors {
  tool: ToolId;
  accuracy: number;
  timing: number;
  stamina: number;
  riverSpeed: number;
  wind: number;
  obstaclePenalty: number;
  injuryPenalty: number;
  character: CharacterDefinition;
}

export interface ProbabilityResult {
  chance: number;
  roll: number;
  success: boolean;
  riskLabel: string;
  breakdown: {
    base: number;
    accuracyBonus: number;
    timingBonus: number;
    characterBonus: number;
    penalties: number;
  };
}

export function evaluateRescue(factors: RescueFactors, random: RandomManager): ProbabilityResult {
  const tool = TOOL_CONFIG[factors.tool];
  const base = Number(tool.baseSuccess);
  const accuracyBonus = (MathUtils.clamp(factors.accuracy, 0, 1) - 0.5) * 0.48;
  const timingBonus = (MathUtils.clamp(factors.timing, 0, 1) - 0.5) * 0.24;
  const toolFit = factors.character.bestTools.includes(factors.tool) ? 0.12 : -0.04;
  const swimBonus = (factors.character.swimSkill - 0.45) * 0.12;
  const staminaBonus = (factors.stamina - 0.5) * 0.16;
  const characterBonus = toolFit + swimBonus + staminaBonus;
  const environmentPenalty =
    factors.riverSpeed * 0.045 +
    factors.wind * 0.035 +
    factors.obstaclePenalty +
    factors.injuryPenalty +
    factors.character.panicLevel * 0.055;
  const chance = MathUtils.clamp(
    base + accuracyBonus + timingBonus + characterBonus - environmentPenalty,
    0.05,
    0.98,
  );
  const roll = random.next();

  // 极好和极差操作设硬边界，中间区间才交给种子随机，避免“完美操作照样频繁翻车”。
  const success = factors.accuracy >= 0.92 && factors.timing >= 0.82
    ? true
    : factors.accuracy <= 0.16
      ? false
      : roll <= chance;

  const riskLabel =
    chance >= 0.8 ? '低风险' : chance >= 0.62 ? '中等风险' : chance >= 0.4 ? '高风险' : '极高风险';

  return {
    chance,
    roll,
    success,
    riskLabel,
    breakdown: {
      base,
      accuracyBonus,
      timingBonus,
      characterBonus,
      penalties: environmentPenalty,
    },
  };
}
